<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useSectionsStore } from "@/stores/sections";
import { usePeopleStore } from "@/stores/people";
import PeopleTable from "@/components/People/PeopleTable.vue";
import SectionTable from "@/components/Section/SectionTable.vue";

const cprRules = /^[0-9]{6}-?[0-9]{4}$/;

interface Props {
  groupId?: string | null;
  level?: string;
}

withDefaults(defineProps<Props>(), { groupId: null, level: "0" });

const router = useRouter();
const sectionsStore = useSectionsStore();
const peopleStore = usePeopleStore();

const searchTerm = ref<string | null>(null);
const disabledSectionIds = ref<string[]>([]);
const searchInput = ref<HTMLInputElement | null>(null);

const sectionsResultFromSearch = computed(() => {
  if (searchTerm.value && searchTerm.value.length) {
    return sectionsStore.mySectionsFromSearch(searchTerm.value);
  }
  return [];
});

const peopleResultFromSearch = computed(() => {
  if (searchTerm.value && searchTerm.value.length) {
    const isCpr = cprRules.test(searchTerm.value);
    const cprResults = isCpr && peopleStore.peopleCpr ? [].concat(peopleStore.peopleCpr as any) : [];
    const people = [
      ...sectionsStore.allStudentsAsPeopleTableObjectsFromSectionsSearched(searchTerm.value.toLowerCase()),
      ...(searchTerm.value.length > 1
        ? sectionsStore.allMyStudentsSearchedAsPeopleTableObejcts(searchTerm.value.toLowerCase())
        : []),
      ...cprResults
    ];
    return people.filter(
      (obj: any, index: number, self: any[]) => index === self.findIndex((t: any) => t.userId === obj.userId)
    );
  }
  return [];
});

const loadSections = async () => {
  await sectionsStore.loadMySections();
  sectionsStore.loadAllSectionsOnInstitution();
};

const onSectionClick = async (clickedSection: any) => {
  router.push({
    name: "confidentialsSectionView",
    params: { sectionId: clickedSection.sectionId }
  });
};

const cancelSearch = async () => {
  searchTerm.value = null;
  searchInput.value?.focus();
};

const onKeyUp = (val: KeyboardEvent) => {
  if (val.keyCode === 27) cancelSearch();
};

watch(searchTerm, newVal => {
  if (newVal && cprRules.test(newVal)) {
    peopleStore.cprSearch(newVal);
  }
});

onMounted(() => {
  loadSections();
});
</script>

<template>
  <div role="search" class="relative">
    <div class="mb-2 w-full md:w-100 mx-auto md:ml-auto md:mr-0 lg:mx-auto relative">
      <font-awesome-icon :icon="['far', 'search']" class="absolute text-gray-500 mt-3 left-14px" />
      <form>
        <input
          ref="searchInput"
          v-model="searchTerm"
          class="w-full outline-none p-2 pl-14 text-gray-600 shadow rounded-sm"
          :placeholder="$t('Search.StudentOrSection')"
          aria-describedby="search-results"
          @keyup="onKeyUp"
        />
        <button
          :aria-label="$t('Search.Clear')"
          class="absolute mt-1 text-xl text-gray-600 right-14px outline-button w-7 h-7"
          @click.prevent="cancelSearch"
        >
          <font-awesome-icon :icon="['fal', 'xmark']" />
        </button>
      </form>
    </div>

    <div
      id="search-results"
      class="nc-card absolute z-10 w-full opacity-97 mb-10"
      style="max-width: 1000px; left: 50%; transform: translateX(calc(-50% - 1px))"
    >
      <div v-if="sectionsResultFromSearch.length">
        <h2 class="ml-5 my-2">{{ $t("Sections.Section.Section") }}</h2>

        <section-table
          :table-caption="$t('Sections.Sections')"
          :sections="sectionsResultFromSearch"
          :disabled-section-ids="disabledSectionIds"
          :is-section-selectable="false"
          :is-section-clickable="true"
          @sectionClick="onSectionClick"
        >
        </section-table>
      </div>

      <div v-if="peopleResultFromSearch.length">
        <h2 class="ml-5 mt-6 mb-2">{{ $t("Sections.Section.Students") }}</h2>

        <people-table
          :table-caption="$t('People.Roles.Students')"
          :students="peopleResultFromSearch"
          :show-groups="false"
          :is-people-selectable="false"
          :is-people-clickable="true"
          on-click-route-name="confidentialsChildView"
          :on-click-route-params="{ childId: ':userId', sectionId: ':sectionId' }"
        >
          <template #column2Header> {{ $t("Sections.Section.Section") }}</template>

          <template #column2Content="{ person }">
            {{ person.sectionName }}
          </template>
        </people-table>
      </div>

      <div
        v-if="searchTerm && !sectionsResultFromSearch.length && !peopleResultFromSearch.length"
        class="w-full text-center text-xs my-4"
        aria-live="assertive"
        aria-atomic="true"
      >
        {{ $t("Search.NoResults") }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.nc-card {
  padding: 0rem;
}

.nc-card-no-results {
  padding: 1rem;
}

@media (min-width: 1280px) {
  .child {
    left: calc(-10vw);
    width: 50vw;
  }
}
</style>
